import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { Fragment, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { encodeAdf } from '@atlassian/conversation-assistant-content-renderer';
import { CardTemplate } from '../../../../constants';
import { messages } from '../../../../messages';
import { useAIAnswerContext } from '../../../ai-answer-context';
import { onAIAnswerReturnLinkClick } from '../../../analytics';
import { NLPSearchResultFormat } from '../../../types';
import { AIAnswerCloseLabelWrapper, AIAnswerResultBlurSection, AIAnswerResultBlurSectionText, AIAnswerResultSection } from '../styled';
import { StatusCitation } from './status-citation';
var ReactRenderer = /*#__PURE__*/React.lazy(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_.renderer" */'@atlaskit/renderer').then(function (_ref) {
    var ReactRenderer = _ref.ReactRenderer;
    return {
      default: ReactRenderer
    };
  });
});
var MarkdownRenderer = /*#__PURE__*/React.lazy(function () {
  return import( /* webpackChunkName: "react-markdown" */'react-markdown').then(function (_ref2) {
    var ReactMarkdown = _ref2.default;
    return {
      default: ReactMarkdown
    };
  });
});
export var RenderedResultComponent = function RenderedResultComponent() {
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isResultOverflow = _useState2[0],
    setResultOverflow = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isResultExpanded = _useState4[0],
    setResultExpanded = _useState4[1];
  var wrapperRef = useRef(null);
  var contentRef = useRef(null);
  var _useAIAnswerContext = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext.commonAttributes,
    fireAnalyticsEvent = _useAIAnswerContext.fireAnalyticsEvent,
    isReadingAids = _useAIAnswerContext.isReadingAids,
    cardTemplate = _useAIAnswerContext.cardTemplate,
    onClose = _useAIAnswerContext.onClose;
  var answerString = commonAttributes.answerString,
    answerFormat = commonAttributes.answerFormat;
  var onCloseDialog = useCallback(function () {
    fireAnalyticsEvent(onAIAnswerReturnLinkClick(commonAttributes));
    onClose === null || onClose === void 0 || onClose();
  }, [commonAttributes, fireAnalyticsEvent, onClose]);
  var enableShowMoreBlur = cardTemplate === CardTemplate.DEFAULT && isReadingAids;
  useEffect(function () {
    if (!wrapperRef.current || !contentRef.current) {
      return;
    }
    var resizeObserver = new ResizeObserver(function () {
      var _wrapperRef$current$o, _wrapperRef$current, _contentRef$current$o, _contentRef$current;
      if (((_wrapperRef$current$o = (_wrapperRef$current = wrapperRef.current) === null || _wrapperRef$current === void 0 ? void 0 : _wrapperRef$current.offsetHeight) !== null && _wrapperRef$current$o !== void 0 ? _wrapperRef$current$o : 0) < ((_contentRef$current$o = (_contentRef$current = contentRef.current) === null || _contentRef$current === void 0 ? void 0 : _contentRef$current.offsetHeight) !== null && _contentRef$current$o !== void 0 ? _contentRef$current$o : 0)) {
        setResultOverflow(true);
      } else {
        setResultOverflow(false);
      }
    });
    resizeObserver.observe(contentRef.current);
    return function () {
      return resizeObserver.disconnect();
    };
  }, []);
  var docContent = useMemo(function () {
    return encodeAdf(answerString, false);
  }, [answerString]);
  var FormattedResult = useMemo(function () {
    var content;
    switch (answerFormat) {
      case NLPSearchResultFormat.ADF:
        content = /*#__PURE__*/React.createElement(ReactRenderer, {
          document: JSON.parse(answerString),
          nodeComponents: {
            // Preventing media and mediaSingle from rendering images due to security vulnerability: https://asecurityteam.atlassian.net/browse/VULN-1543153
            // Followup ticket to re-enable image rendering: https://hello.jira.atlassian.cloud/browse/CCSEARCH-4799
            media: function media() {
              return /*#__PURE__*/React.createElement(Fragment, null);
            },
            mediaSingle: function mediaSingle() {
              return /*#__PURE__*/React.createElement(Fragment, null);
            }
          }
        });
        break;
      case NLPSearchResultFormat.MARKDOWN:
        if (!isReadingAids && fg('kd_smart_answers_use_adf_encoder')) {
          content = /*#__PURE__*/React.createElement(SmartCardProvider, null, /*#__PURE__*/React.createElement(ReactRenderer, {
            document: docContent,
            nodeComponents: {
              status: function status(props) {
                return /*#__PURE__*/React.createElement(StatusCitation, props);
              },
              // Preventing media and mediaSingle from rendering images due to security vulnerability: https://asecurityteam.atlassian.net/browse/VULN-1543153
              // Followup ticket to re-enable img rendering: https://hello.jira.atlassian.cloud/browse/CCSEARCH-4799
              media: function media() {
                return /*#__PURE__*/React.createElement(Fragment, null);
              },
              mediaSingle: function mediaSingle() {
                return /*#__PURE__*/React.createElement(Fragment, null);
              }
            }
          }));
          break;
        }
        // Preventing img elements from rendering due to security vulnerability: https://asecurityteam.atlassian.net/browse/VULN-1543153
        // Followup ticket to re-enable img rendering: https://hello.jira.atlassian.cloud/browse/CCSEARCH-4799
        content = /*#__PURE__*/React.createElement(MarkdownRenderer, {
          disallowedElements: ['img']
        }, answerString);
        break;
      default:
        return /*#__PURE__*/React.createElement(Fragment, null);
    }
    return /*#__PURE__*/React.createElement("div", {
      ref: contentRef
    }, /*#__PURE__*/React.createElement(Suspense, {
      fallback: /*#__PURE__*/React.createElement(Fragment, null)
    }, content));
  }, [answerFormat, answerString, contentRef, docContent, isReadingAids]);
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(AIAnswerResultSection, {
    id: "ai-answer-result-section",
    ref: wrapperRef,
    isReadingAids: isReadingAids && cardTemplate === CardTemplate.DEFAULT,
    isExpanded: isResultExpanded
  }, FormattedResult), enableShowMoreBlur && isResultOverflow && !isResultExpanded && /*#__PURE__*/React.createElement(AIAnswerResultBlurSection, {
    onClick: function onClick() {
      return setResultExpanded(true);
    }
  }, /*#__PURE__*/React.createElement(AIAnswerResultBlurSectionText, null, formatMessage(messages.ai_answer_expand_button_label))), !isReadingAids && onClose && /*#__PURE__*/React.createElement(AIAnswerCloseLabelWrapper, null, /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.ai_answer_dialog_cancel_label, {
    values: {
      searchResultsLink:
      /*#__PURE__*/
      // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      React.createElement("a", {
        onClick: onCloseDialog
      }, formatMessage(messages.ai_answer_dialog_search_results_label))
    }
  }))));
};